import { useEffect, useState, useCallback } from 'react';
import Layout from '@/components/Layout';
import { Documento, DocumentStatus } from '@/types';
import { getDocumentos, getClientes, deleteDocumento } from '@/lib/supabaseService';
import { supabase, resolveDocUrl } from '@/lib/supabase';
import DocumentViewer from '@/components/DocumentViewer';
import Dialog from '@/components/Dialog';
import toast from 'react-hot-toast';
import { useRouter } from 'next/router';
import { useAuth } from '@/lib/authContext';

interface Cliente {
  id: string;
  razon_social: string;
  nombre_comercial: string;
  nif: string;
}

interface DocumentoDB {
  id: string;
  nombre: string;
  cliente_id: string;
  tipo: string;
  estado: string;
  fecha_subida: string;
  procesado_ia: boolean;
  tamanio: number;
  url_archivo: string;
  datos_ia?: any;
  nro_asiento_erp?: string;
}

export default function AdminDocumentos() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [documentos, setDocumentos] = useState<DocumentoDB[]>([]);
  const [clientes, setClientes] = useState<Map<string, Cliente>>(new Map());
  const [asesoresMap, setAsesoresMap] = useState<Map<string, string>>(new Map()); // advisor ID -> advisor name
  const [clientesAsesoresMap, setClientesAsesoresMap] = useState<Map<string, string[]>>(new Map()); // client ID -> advisor IDs
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState<DocumentoDB | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<string | null>(null);
  const [vistaActual, setVistaActual] = useState<'clientes' | 'documentos'>('clientes');
  const [documentosPendientes, setDocumentosPendientes] = useState<DocumentoDB[]>([]);
  const [indiceDocumentoActual, setIndiceDocumentoActual] = useState<number>(0);
  const [seleccionados, setSeleccionados] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Convert database format to Documento interface format
  const mapDocumentoToInterface = (doc: DocumentoDB): Documento => {
    return {
      id: doc.id,
      nombre: doc.nombre,
      clienteId: doc.cliente_id,
      asesorId: null,
      tipo: doc.tipo as any,
      estado: doc.estado as any,
      fechaSubida: new Date(doc.fecha_subida),
      procesadoIA: doc.procesado_ia,
      tamaño: doc.tamanio,
      urlArchivo: doc.url_archivo,
      nroAsientoERP: doc.nro_asiento_erp,
      datosIA: doc.datos_ia,
    };
  };
  
  const [formularioAsiento, setFormularioAsiento] = useState({
    tipoDocumento: '',
    asiento: '',
    notas: '',
  });

  const loadDocuments = useCallback(async () => {
    try {
      setLoadingData(true);
      
      // Get all clients (admin can see all)
      const { data: allClients, error: clientError } = await supabase
        .from('clientes')
        .select('*');

      if (clientError) {
        console.error('Error loading clients:', clientError);
        throw clientError;
      }

      // Build clients map
      const clientsMap = new Map<string, Cliente>();
      allClients?.forEach(cliente => {
        clientsMap.set(cliente.id, cliente as any);
      });
      setClientes(clientsMap);

      // Get advisor-client assignments
      const { data: asignaciones, error: asignError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, asesor_id');

      if (asignError) {
        console.error('Error loading advisor assignments:', asignError);
      } else {
        // Build client -> advisor IDs map
        const clientAsesorMap = new Map<string, string[]>();
        asignaciones?.forEach(asignacion => {
          const current = clientAsesorMap.get(asignacion.cliente_id) || [];
          if (!current.includes(asignacion.asesor_id)) {
            clientAsesorMap.set(asignacion.cliente_id, [...current, asignacion.asesor_id]);
          }
        });
        setClientesAsesoresMap(clientAsesorMap);

        // Get all unique advisor IDs
        const advisorIds = [...new Set(asignaciones?.map(a => a.asesor_id) || [])];
        
        if (advisorIds.length > 0) {
          // Fetch advisor names
          const { data: advisors, error: advisorError } = await supabase
            .from('profiles')
            .select('id, nombre')
            .in('id', advisorIds)
            .eq('rol', 'asesor');

          if (advisorError) {
            console.error('Error loading advisors:', advisorError);
          } else {
            // Build advisor ID -> name map
            const advisorMap = new Map<string, string>();
            advisors?.forEach(advisor => {
              advisorMap.set(advisor.id, advisor.nombre || 'Sin nombre');
            });
            setAsesoresMap(advisorMap);
          }
        }
      }

      // Get all documents for manual accounting (both pending and processed)
      const { data: allDocs, error: docsError } = await supabase
        .from('documentos')
        .select('*')
        .in('estado', ['pendiente', 'contabilizado']);

      if (docsError) {
        console.error('Error loading documents:', docsError);
        throw docsError;
      }

      setDocumentos(allDocs || []);
    } catch (error: any) {
      console.error('Error loading documents:', error);
      toast.error(`Error al cargar documentos: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'admin')) {
      router.push('/');
    }
    if (user && user.rol === 'admin') {
      loadDocuments();
    }
  }, [user, loading, router, loadDocuments]);

  // Handle cliente query parameter
  useEffect(() => {
    if (router.query.cliente && typeof router.query.cliente === 'string' && !loadingData) {
      const clienteId = router.query.cliente;
      if (clientes.size > 0 && clientes.has(clienteId)) {
        setClienteSeleccionado(clienteId);
        const docsCliente = getDocumentosDelCliente(clienteId).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
        setIndiceDocumentoActual(0);
        setSeleccionados(new Set());
        setVistaActual('documentos');
      }
    }
  }, [router.query.cliente, clientes, loadingData]);

  const handleVerDocumento = (doc: DocumentoDB, index?: number) => {
    setDocumentoSeleccionado(doc);
    if (index !== undefined) {
      setIndiceDocumentoActual(index);
    }
    setMostrarFormulario(true);
    // Reset form with AI data if available
    if (doc.datos_ia) {
      setFormularioAsiento({
        tipoDocumento: doc.datos_ia.tipoDocumento || '',
        asiento: doc.nro_asiento_erp || '',
        notas: doc.datos_ia.notas || '',
      });
    } else {
      setFormularioAsiento({
        tipoDocumento: '',
        asiento: '',
        notas: '',
      });
    }
  };

  const calcularSiguienteNumeroAsiento = (): string => {
    if (!formularioAsiento.asiento) return '';
    
    const match = formularioAsiento.asiento.match(/(\d+)$/);
    if (match) {
      const numero = parseInt(match[1], 10);
      const siguienteNumero = numero + 1;
      return formularioAsiento.asiento.replace(/\d+$/, siguienteNumero.toString());
    }
    
    const asNumero = parseInt(formularioAsiento.asiento, 10);
    if (!isNaN(asNumero)) {
      return (asNumero + 1).toString();
    }
    
    return formularioAsiento.asiento;
  };

  const handleSiguienteDocumento = () => {
    const siguienteNumero = calcularSiguienteNumeroAsiento();
    
    if (indiceDocumentoActual < documentosPendientes.length - 1) {
      const siguiente = documentosPendientes[indiceDocumentoActual + 1];
      setIndiceDocumentoActual(indiceDocumentoActual + 1);
      setDocumentoSeleccionado(siguiente);
      
      setFormularioAsiento({
        tipoDocumento: formularioAsiento.tipoDocumento,
        asiento: siguienteNumero,
        notas: '',
      });
    } else {
      toast.success('No hay más documentos pendientes');
      setMostrarFormulario(false);
      setDocumentoSeleccionado(null);
    }
  };

  const handleTerminar = () => {
    setMostrarFormulario(false);
    setDocumentoSeleccionado(null);
  };

  const handleEliminarDocumentoActual = async () => {
    if (!documentoSeleccionado) return;
    
    if (!confirm('¿Estás seguro de que deseas eliminar este documento?')) {
      return;
    }

    try {
      await deleteDocumento(documentoSeleccionado.id);
      toast.success('Documento eliminado');
      
      const nuevaLista = documentosPendientes.filter(d => d.id !== documentoSeleccionado.id);
      setDocumentosPendientes(nuevaLista);
      
      await loadDocuments();
      
      if (nuevaLista.length > 0) {
        if (indiceDocumentoActual < nuevaLista.length) {
          handleVerDocumento(nuevaLista[indiceDocumentoActual], indiceDocumentoActual);
        } else {
          handleVerDocumento(nuevaLista[nuevaLista.length - 1], nuevaLista.length - 1);
        }
      } else {
        toast('No hay más documentos pendientes');
        handleVolverAClientes();
      }
    } catch (error: any) {
      toast.error(`Error al eliminar: ${error.message}`);
    }
  };

  const handleGuardarAsiento = async () => {
    try {
      if (!documentoSeleccionado) return;

      if (!formularioAsiento.tipoDocumento) {
        toast.error('Por favor selecciona el tipo de documento');
        return;
      }
      if (!formularioAsiento.asiento) {
        toast.error('Por favor ingresa el número de asiento');
        return;
      }

      const existingDatosIA = documentoSeleccionado.datos_ia || {};
      const datosIA = {
        ...existingDatosIA,
        tipoDocumento: formularioAsiento.tipoDocumento,
        notas: formularioAsiento.notas,
        nivelConfianza: 100,
      };

      let response: Response;
      try {
        response = await fetch('/api/documents/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentoId: documentoSeleccionado.id,
            updates: {
              estado: 'contabilizado',
              procesadoIA: true,
              nroAsientoERP: formularioAsiento.asiento,
              datosIA: datosIA,
            },
          }),
        });
      } catch (fetchError: any) {
        console.error('Network error:', fetchError);
        throw new Error('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
      }

      if (!response.ok) {
        let errorMessage = 'Error al actualizar el documento';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          errorMessage = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Error al actualizar el documento');
      }

      toast.success('Documento contabilizado y enviado a ERP Cegid Diez');
      
      const siguienteNumero = calcularSiguienteNumeroAsiento();
      
      const nuevaLista = documentosPendientes.filter(d => d.id !== documentoSeleccionado.id);
      setDocumentosPendientes(nuevaLista);
      
      await loadDocuments();
      
      if (nuevaLista.length > 0) {
        const siguienteDoc = indiceDocumentoActual < nuevaLista.length 
          ? nuevaLista[indiceDocumentoActual] 
          : nuevaLista[nuevaLista.length - 1];
        
        setDocumentoSeleccionado(siguienteDoc);
        
        setFormularioAsiento({
          tipoDocumento: formularioAsiento.tipoDocumento,
          asiento: siguienteNumero,
          notas: '',
        });
      } else {
        toast('No hay más documentos pendientes');
        handleVolverAClientes();
        
        setFormularioAsiento({
          tipoDocumento: '',
          asiento: '',
          notas: '',
        });
      }
    } catch (error: any) {
      console.error('Error saving entry:', error);
      const errorMessage = error.message || 'Error desconocido al guardar el asiento contable';
      toast.error(`Error: ${errorMessage}`);
    }
  };

  const handleEliminarSeleccionados = async () => {
    if (seleccionados.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    if (!confirm(`¿Estás seguro de que deseas eliminar ${seleccionados.size} documento(s)?`)) {
      return;
    }

    setBulkActionLoading(true);
    toast.loading('Eliminando...');
    try {
      const ids = Array.from(seleccionados);
      let ok = 0;
      let err = 0;
      for (const docId of ids) {
        try {
          await deleteDocumento(docId);
          ok++;
        } catch {
          err++;
        }
      }
      toast.dismiss();
      if (err) toast.error(`Eliminados: ${ok}. Fallos: ${err}`);
      else toast.success(`${ok} documento(s) eliminado(s)`);
      setSeleccionados(new Set());
      await loadDocuments();
      if (clienteSeleccionado) {
        const docsCliente = getDocumentosDelCliente(clienteSeleccionado).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
      }
    } catch (error: any) {
      toast.dismiss();
      toast.error('Error al eliminar documentos');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleDescargarZip = async () => {
    if (seleccionados.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }

    const ids = Array.from(seleccionados);
    setBulkActionLoading(true);
    toast.loading('Preparando ZIP...');
    try {
      const res = await fetch('/api/documents/download-zip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ documentoIds: ids }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Error al descargar ZIP');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documentos-contabilizacion-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('ZIP descargado');
    } catch (error: any) {
      toast.dismiss();
      toast.error(error.message || 'Error al descargar ZIP');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const toggleSeleccion = (docId: string) => {
    const newSeleccionados = new Set(seleccionados);
    if (newSeleccionados.has(docId)) {
      newSeleccionados.delete(docId);
    } else {
      newSeleccionados.add(docId);
    }
    setSeleccionados(newSeleccionados);
  };

  const handleContabilizarDocumento = async (doc: DocumentoDB, index: number) => {
    handleVerDocumento(doc, index);
  };

  const handleEliminarDocumento = async (docId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este documento?')) {
      return;
    }

    try {
      await deleteDocumento(docId);
      toast.success('Documento eliminado');
      await loadDocuments();
      
      if (clienteSeleccionado) {
        const docsCliente = getDocumentosDelCliente(clienteSeleccionado).filter(d => d.estado === 'pendiente');
        setDocumentosPendientes(docsCliente);
      }
    } catch (error: any) {
      toast.error(`Error al eliminar: ${error.message}`);
    }
  };

  const getClienteById = (id: string): Cliente | undefined => {
    return clientes.get(id);
  };

  interface ClienteStats {
    cliente: Cliente;
    totalDocumentos: number;
    pendientes: number;
    procesados: number;
    ultimoDocumento?: Date;
  }

  const getClienteStats = (): ClienteStats[] => {
    const statsMap = new Map<string, ClienteStats>();

    clientes.forEach((cliente, clienteId) => {
      statsMap.set(clienteId, {
        cliente,
        totalDocumentos: 0,
        pendientes: 0,
        procesados: 0,
      });
    });

    documentos.forEach((doc) => {
      const stats = statsMap.get(doc.cliente_id);
      if (stats) {
        stats.totalDocumentos++;
        if (doc.estado === 'contabilizado') {
          stats.procesados++;
        } else {
          stats.pendientes++;
        }
        const docDate = new Date(doc.fecha_subida);
        if (!stats.ultimoDocumento || docDate > stats.ultimoDocumento) {
          stats.ultimoDocumento = docDate;
        }
      }
    });

    return Array.from(statsMap.values())
      .sort((a, b) => {
        if (b.pendientes !== a.pendientes) {
          return b.pendientes - a.pendientes;
        }
        return b.totalDocumentos - a.totalDocumentos;
      });
  };

  const handleClienteClick = (clienteId: string) => {
    setClienteSeleccionado(clienteId);
    const docsCliente = getDocumentosDelCliente(clienteId).filter(d => d.estado === 'pendiente');
    setDocumentosPendientes(docsCliente);
    setIndiceDocumentoActual(0);
    setSeleccionados(new Set());
    setVistaActual('documentos');
    router.push(`/admin/documentos?cliente=${clienteId}`, undefined, { shallow: true });
  };

  const handleVolverAClientes = () => {
    setClienteSeleccionado(null);
    setVistaActual('clientes');
    setMostrarFormulario(false);
    setDocumentoSeleccionado(null);
    setDocumentosPendientes([]);
    setIndiceDocumentoActual(0);
    setSeleccionados(new Set());
  };

  const getTipoDocumentoInfo = (tipo: string) => {
    if (tipo === 'emitida_pdf') return 'Facturas emitidas (PDF)';
    if (tipo === 'recibida_pdf') return 'Factura recibidas (PDF)';
    if (tipo === 'emitida_excel') return 'Facturas emitidas (Excel)';
    if (tipo === 'recibida_excel') return 'Facturas recibidas (Excel)';
    return 'Otros';
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedDocumentos = (docs: DocumentoDB[]): DocumentoDB[] => {
    if (!sortColumn) return docs;

    return [...docs].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'documento':
          aValue = a.nombre.toLowerCase();
          bValue = b.nombre.toLowerCase();
          break;
        case 'empresa':
          aValue = getClienteById(a.cliente_id)?.razon_social?.toLowerCase() || '';
          bValue = getClienteById(b.cliente_id)?.razon_social?.toLowerCase() || '';
          break;
        case 'tipo':
          aValue = getTipoDocumentoInfo(a.tipo).toLowerCase();
          bValue = getTipoDocumentoInfo(b.tipo).toLowerCase();
          break;
        case 'subido':
          aValue = new Date(a.fecha_subida).getTime();
          bValue = new Date(b.fecha_subida).getTime();
          break;
        case 'estado':
          aValue = a.estado.toLowerCase();
          bValue = b.estado.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getDocumentosDelCliente = (clienteId: string): DocumentoDB[] => {
    const filtered = documentos.filter((doc) => doc.cliente_id === clienteId);
    return getSortedDocumentos(filtered);
  };

  if (loading || loadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-calm-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-admin mx-auto"></div>
          <p className="mt-4 text-calm-muted">Cargando documentos...</p>
        </div>
      </div>
    );
  }

  const clienteStats = getClienteStats();
  const totalPendientes = documentos.filter(d => d.estado !== 'contabilizado').length;
  const totalProcesados = documentos.filter(d => d.estado === 'contabilizado').length;

  return (
    <Layout rol="admin">
      <div className="h-[calc(100vh-72px)] overflow-y-auto space-y-6 p-4 md:p-8">
        <div>
          <h1 className="text-5xl font-light font-display text-calm-text">Contabilización Manual</h1>
          <p className="text-calm-muted mt-2">Registro manual de facturas e integración ERP</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
            <div className="text-[11px] uppercase tracking-widest text-calm-muted">Pendientes de Contabilizar</div>
            <div className="text-2xl font-light text-calm-text mt-1">{totalPendientes}</div>
          </div>
          <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
            <div className="text-[11px] uppercase tracking-widest text-calm-muted">Sin Procesar IA</div>
            <div className="text-2xl font-light text-yellow-600 mt-1">{totalPendientes}</div>
          </div>
          <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
            <div className="text-[11px] uppercase tracking-widest text-calm-muted">Integración ERP</div>
            <div className="text-sm font-medium text-success mt-1"><span className="material-symbols-outlined text-sm align-middle">check</span> Cegid Diez</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-8">
          <div className="flex items-start space-x-3">
            <div className="text-2xl"><span className="material-symbols-outlined text-2xl">warning</span></div>
            <div>
              <h3 className="font-light text-yellow-900 mb-1">Contabilización Manual</h3>
              <p className="text-sm text-yellow-800">
                Estos documentos no fueron procesados automáticamente por la IA y requieren entrada manual.
                Asegúrate de verificar todos los datos antes de enviar a ERP.
              </p>
            </div>
          </div>
        </div>

        {vistaActual === 'clientes' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {clienteStats.map((stats) => {
              const iniciales = stats.cliente.razon_social
                .split(' ')
                .map((palabra) => palabra[0])
                .join('')
                .substring(0, 2)
                .toUpperCase();

              return (
                <div
                  key={stats.cliente.id}
                  className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => handleClienteClick(stats.cliente.id)}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-fresh-sky-400 to-fresh-sky-500 flex items-center justify-center text-white font-light text-lg">
                      {iniciales}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-light text-calm-text">
                        {stats.cliente.razon_social}
                      </h3>
                      <p className="text-sm text-calm-muted">{stats.cliente.nif}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-calm-muted">Último documento</span>
                      <span className="text-sm text-calm-text">
                        {stats.ultimoDocumento
                          ? `hace ${Math.floor(
                              (Date.now() - stats.ultimoDocumento.getTime()) /
                                (1000 * 60 * 60 * 24)
                            )} días`
                          : '-'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-calm-muted">Asesor asignado</span>
                      <span className="text-sm text-calm-text font-medium">
                        {(() => {
                          const advisorIds = clientesAsesoresMap.get(stats.cliente.id) || [];
                          if (advisorIds.length === 0) {
                            return <span className="text-calm-muted italic">Sin asignar</span>;
                          }
                          const advisorNames = advisorIds
                            .map(id => asesoresMap.get(id))
                            .filter(Boolean)
                            .join(', ');
                          return advisorNames || <span className="text-calm-muted italic">Sin asignar</span>;
                        })()}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-calm-border flex justify-between items-center">
                    <div className="text-center flex-1">
                      <div className="text-2xl font-light text-orange-600">
                        {stats.pendientes}
                      </div>
                      <div className="text-xs text-calm-muted mt-1">a contabilizar</div>
                    </div>
                    <div className="h-8 w-px bg-calm-border"></div>
                    <div className="text-center flex-1">
                      <div className="text-2xl font-light text-green-600">
                        {stats.procesados}
                      </div>
                      <div className="text-xs text-calm-muted mt-1">procesados</div>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClienteClick(stats.cliente.id);
                    }}
                    className="btn btn-sm btn-admin w-full mt-4"
                  >
                    Trabajar
                  </button>
                </div>
              );
            })}

            {clienteStats.length === 0 && (
              <div className="col-span-full text-center py-12">
                <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto"><span className="material-symbols-outlined text-4xl">group</span></div>
                <h3 className="text-xl font-light text-calm-text mb-2">
                  No hay clientes
                </h3>
                <p className="text-calm-muted">
                  No hay clientes registrados en el sistema.
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {clienteSeleccionado && (
              <>
                <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
                  <div className="flex items-center justify-between border-b border-calm-border pb-4 mb-4">
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={handleVolverAClientes}
                        className="text-calm-muted hover:text-calm-text"
                      >
                        ← Volver a Clientes
                      </button>
                      <div>
                        <h2 className="text-lg font-light text-calm-text">
                          Gestor de Contabilizacion documental <span className="text-orange-500">Trabajar</span>
                        </h2>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        className="px-4 py-2 text-sm text-orange-500 font-medium border-b-2 border-orange-500"
                      >
                        Trabajar
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <p className="text-sm text-calm-text">
                      <span className="font-light text-orange-500 cursor-pointer hover:text-orange-600 hover:underline">
                        ADMIN {user?.nombre?.toUpperCase() || 'ADMIN'}
                      </span> EMPRESA O AUTÓNOMO <span 
                        className="font-light cursor-pointer hover:text-calm-text hover:underline"
                      >
                        {getClienteById(clienteSeleccionado)?.razon_social?.toUpperCase()}
                      </span> CON IDENTIFICACIÓN FISCAL <span 
                        className="font-light cursor-pointer hover:text-calm-text hover:underline"
                      >
                        {getClienteById(clienteSeleccionado)?.nif}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-8">
                  <div className="flex items-center justify-between mb-4 pb-4 border-b border-calm-border">
                    <h3 className="text-[11px] uppercase tracking-widest text-calm-muted">
                      Documentos Pendientes de Contabilizar
                    </h3>
                  </div>

                  {documentosPendientes.length > 0 ? (
                    <>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-calm-border bg-gradient-to-r from-steel-azure-50/50 to-fresh-sky-50/30">
                              <th className="text-left py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted">
                                <input
                                  type="checkbox"
                                  checked={seleccionados.size === documentosPendientes.length}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSeleccionados(new Set(documentosPendientes.map(d => d.id)));
                                    } else {
                                      setSeleccionados(new Set());
                                    }
                                  }}
                                  className="rounded-huge"
                                />
                              </th>
                              <th 
                                className="text-left py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted cursor-pointer hover:bg-calm-bg select-none"
                                onClick={() => handleSort('documento')}
                              >
                                <div className="flex items-center space-x-1">
                                  <span>Documento</span>
                                  {sortColumn === 'documento' && (
                                    <span>{sortDirection === 'asc' ? <span className="material-symbols-outlined text-sm align-middle">arrow_upward</span> : <span className="material-symbols-outlined text-sm align-middle">arrow_downward</span>}</span>
                                  )}
                                </div>
                              </th>
                              <th 
                                className="text-left py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted cursor-pointer hover:bg-calm-bg select-none"
                                onClick={() => handleSort('empresa')}
                              >
                                <div className="flex items-center space-x-1">
                                  <span>Nombre Empresa</span>
                                  {sortColumn === 'empresa' && (
                                    <span>{sortDirection === 'asc' ? <span className="material-symbols-outlined text-sm align-middle">arrow_upward</span> : <span className="material-symbols-outlined text-sm align-middle">arrow_downward</span>}</span>
                                  )}
                                </div>
                              </th>
                              <th 
                                className="text-left py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted cursor-pointer hover:bg-calm-bg select-none"
                                onClick={() => handleSort('tipo')}
                              >
                                <div className="flex items-center space-x-1">
                                  <span>Tipo de Documento</span>
                                  {sortColumn === 'tipo' && (
                                    <span>{sortDirection === 'asc' ? <span className="material-symbols-outlined text-sm align-middle">arrow_upward</span> : <span className="material-symbols-outlined text-sm align-middle">arrow_downward</span>}</span>
                                  )}
                                </div>
                              </th>
                              <th 
                                className="text-left py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted cursor-pointer hover:bg-calm-bg select-none"
                                onClick={() => handleSort('subido')}
                              >
                                <div className="flex items-center space-x-1">
                                  <span>Subido</span>
                                  {sortColumn === 'subido' && (
                                    <span>{sortDirection === 'asc' ? <span className="material-symbols-outlined text-sm align-middle">arrow_upward</span> : <span className="material-symbols-outlined text-sm align-middle">arrow_downward</span>}</span>
                                  )}
                                </div>
                              </th>
                              <th 
                                className="text-left py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted cursor-pointer hover:bg-calm-bg select-none"
                                onClick={() => handleSort('estado')}
                              >
                                <div className="flex items-center space-x-1">
                                  <span>Estado</span>
                                  {sortColumn === 'estado' && (
                                    <span>{sortDirection === 'asc' ? <span className="material-symbols-outlined text-sm align-middle">arrow_upward</span> : <span className="material-symbols-outlined text-sm align-middle">arrow_downward</span>}</span>
                                  )}
                                </div>
                              </th>
                              <th className="text-right py-3 px-4 text-[11px] uppercase tracking-widest text-calm-muted">
                                Acciones
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {getSortedDocumentos(documentosPendientes).map((doc, index) => {
                              const cliente = getClienteById(doc.cliente_id);
                              return (
                                <tr
                                  key={doc.id}
                                  className="border-b border-calm-border hover:bg-calm-bg transition-colors"
                                >
                                  <td className="py-3 px-4">
                                    <input
                                      type="checkbox"
                                      checked={seleccionados.has(doc.id)}
                                      onChange={() => toggleSeleccion(doc.id)}
                                      className="rounded-huge"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td className="py-3 px-4">
                                    <button
                                      onClick={() => window.open(resolveDocUrl(doc.url_archivo), '_blank')}
                                      className="text-calm-accent hover:text-calm-accent hover:underline text-left text-sm"
                                    >
                                      {doc.nombre}
                                    </button>
                                  </td>
                                  <td className="py-3 px-4 text-sm text-calm-text">
                                    {cliente?.razon_social || 'N/A'}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-calm-text">
                                    {getTipoDocumentoInfo(doc.tipo)}
                                  </td>
                                  <td className="py-3 px-4 text-sm text-calm-muted">
                                    {new Date(doc.fecha_subida).toLocaleString('es-ES', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className="inline-flex px-3 py-1 text-xs font-medium rounded-huge bg-green-100 text-green-700">
                                      En revisión
                                    </span>
                                  </td>
                                  <td className="py-3 px-4">
                                    <div className="flex items-center justify-end space-x-2">
                                      <button
                                        onClick={() => window.open(resolveDocUrl(doc.url_archivo), '_blank')}
                                        className="text-calm-accent hover:text-calm-accent p-1.5 rounded-huge hover:bg-calm-bg"
                                        title="Ver documento"
                                      >
                                        <span className="material-symbols-outlined text-sm align-middle">visibility</span>
                                      </button>
                                      <button
                                        onClick={() => handleEliminarDocumento(doc.id)}
                                        className="text-red-600 hover:text-red-800 p-1.5 rounded-huge hover:bg-red-50"
                                        title="Eliminar"
                                      >
                                        <span className="material-symbols-outlined text-sm align-middle">delete</span>
                                      </button>
                                      <button
                                        onClick={() => handleContabilizarDocumento(doc, index)}
                                        className="text-green-600 hover:text-green-800 p-1.5 rounded-huge hover:bg-green-50"
                                        title="Contabilizar"
                                      >
                                        <span className="material-symbols-outlined text-sm align-middle">edit_note</span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-4 flex justify-start">
                        {seleccionados.size > 0 && (
                          <div className="flex flex-wrap items-center gap-3">
                            <span className="text-sm font-medium text-calm-text">
                              {seleccionados.size} documento(s) seleccionado(s)
                            </span>
                            <button
                              onClick={handleDescargarZip}
                              disabled={bulkActionLoading}
                              className="btn btn-sm btn-admin"
                            >
                              <span className="material-symbols-outlined text-sm align-middle">package</span> Descargar ZIP
                            </button>
                            <button
                              onClick={handleEliminarSeleccionados}
                              disabled={bulkActionLoading}
                              className="btn btn-sm btn-danger"
                            >
                              Eliminar seleccionados
                            </button>
                            <button
                              onClick={() => setSeleccionados(new Set())}
                              disabled={bulkActionLoading}
                              className="ghost-btn text-sm"
                            >
                              Deseleccionar
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto"><span className="material-symbols-outlined text-4xl">check_circle</span></div>
                      <h3 className="text-xl font-light text-calm-text mb-2">
                        No hay documentos pendientes
                      </h3>
                      <p className="text-calm-muted">
                        Todos los documentos de este cliente han sido contabilizados.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        <Dialog
          isOpen={mostrarFormulario && !!documentoSeleccionado}
          onClose={() => {
            setMostrarFormulario(false);
            setDocumentoSeleccionado(null);
          }}
          title={`Documento a contabilizar`}
          maxWidth="7xl"
        >
          {documentoSeleccionado && (
            <div className="grid grid-cols-12 gap-6 h-[70vh]">
              <div className="col-span-4 flex flex-col">
                <div className="flex-1 space-y-4 overflow-y-auto">
                  <div className="bg-calm-bg border border-calm-border rounded-huge p-3">
                    <h4 className="font-light text-calm-text mb-1 text-sm">
                      Gestión de los documentos a contabilizar.
                    </h4>
                    <p className="text-calm-text text-xs">
                      ¿Incidencia?
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-calm-text mb-2">
                      Tipo documento: <span className="text-red-500">*</span>
                    </label>
                    <select
                      className="input w-full"
                      value={formularioAsiento.tipoDocumento}
                      onChange={(e) => setFormularioAsiento({ ...formularioAsiento, tipoDocumento: e.target.value })}
                    >
                      <option value="">Seleccionar tipo...</option>
                      <option value="emitida_pdf">Facturas emitidas (PDF)</option>
                      <option value="recibida_pdf">Factura recibidas (PDF)</option>
                      <option value="emitida_excel">Facturas emitidas (Excel)</option>
                      <option value="recibida_excel">Facturas recibidas (Excel)</option>
                      <option value="otro">Otros</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-calm-text mb-2">
                      Asiento: <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formularioAsiento.asiento}
                      onChange={(e) => setFormularioAsiento({ ...formularioAsiento, asiento: e.target.value })}
                      placeholder="Número de asiento"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-calm-text mb-2">
                      Notas:
                    </label>
                    <textarea
                      className="input w-full"
                      rows={4}
                      value={formularioAsiento.notas}
                      onChange={(e) => setFormularioAsiento({ ...formularioAsiento, notas: e.target.value })}
                      placeholder="El obligatorio explicar el motivo de la incidencia. El cliente recibirá esta nota."
                    />
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-calm-border">
                  <div className="flex space-x-2">
                    <button
                      onClick={handleTerminar}
                      className="btn btn-sm flex-1"
                      style={{ 
                        backgroundColor: '#6b7280', 
                        color: 'white'
                      }}
                    >
                      Terminar
                    </button>
                    <button
                      onClick={handleSiguienteDocumento}
                      className="btn btn-sm flex-1"
                      style={{ 
                        backgroundColor: '#3b82f6', 
                        color: 'white'
                      }}
                      disabled={indiceDocumentoActual >= documentosPendientes.length - 1}
                    >
                      {indiceDocumentoActual >= documentosPendientes.length - 1 
                        ? 'Último documento' 
                        : formularioAsiento.asiento 
                          ? `Siguiente ${calcularSiguienteNumeroAsiento()}, quedan ${documentosPendientes.length - indiceDocumentoActual - 1}`
                          : `Siguiente, quedan ${documentosPendientes.length - indiceDocumentoActual - 1}`}
                    </button>
                  </div>
                  <div className="mt-2">
                    <button
                      onClick={() => window.open(resolveDocUrl(documentoSeleccionado.url_archivo), '_blank')}
                      className="btn btn-primary"
                    >
                      Abrir Documento
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Dialog>
      </div>
    </Layout>
  );
}
